import QuestionTypeIcon from '../../../../components/Votings/QuestionTypeIcon';
import FormCheckboxField from '../../../../components/form/FormCheckboxField';
import FormTextField from '../../../../components/form/FormTextField';
import {
  MAX_SCALE_MAX,
  MAX_SCALE_MIN,
  MIN_SCALE_MAX,
  MIN_SCALE_MIN,
  QUESTION_TYPES,
  answerRuleHint,
  questionTypeLabel,
} from '../../../../lib/votings';
import { __, composeValidators, isLink, required } from '../../../../utils';
import {
  questionShape,
  retypeQuestion,
  type QuestionValues,
} from './formValues';
import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { FieldArray } from 'react-final-form-arrays';

type Props = {
  /** Prefix poľa vo formulári, napr. `questions.2`. */
  name: string;
  index: number;
  total: number;
  value: QuestionValues;
  onUpdate: (next: QuestionValues) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  disabled?: boolean;
};

/**
 * Každé pole v tomto editore dostáva `sx={{ mb: 0 }}`.
 *
 * `Field` z knižnice si nesie vlastný spodný odstup 18 px (odstup pod poľom
 * nesie pole samo, nie jeho obal - viď jeho dokumentáciu). V zvislom formulári
 * je to správne, ale tu sú polia v **riadkoch s ikonami**: obal poľa je potom
 * o 18 px vyšší než samotný vstup a `alignItems: 'center'` centruje kôš voči
 * tomu vyššiemu obalu, takže sedí o 9 px nižšie než pole. Odstupy si tu preto
 * riadi `gap` okolitého `Stack`-u.
 */
const SectionLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'grey.600' }}>
    {children}
  </Typography>
);

/**
 * Editor jednej otázky - pravý panel buildera.
 *
 * **Typ otázky je štvorica dlaždíc, nie select v popovere.** Kým bol schovaný
 * za ozubeným kolieskom, nedalo sa z obrazovky prečítať, akého typu otázka je,
 * a „výber viacerých možností" sa nedal nájsť vôbec - dlho to bola jedna
 * položka s číselníkmi pod ňou. Dlaždica nesie tú istú ikonu, akú uvidí občan
 * pri odpovedi, takže typ je viditeľný na oboch stranách rovnako.
 *
 * Prepnutie typu ide cez `retypeQuestion`, nie cez pole formulára: prechod
 * medzi typmi mení štruktúru (počet možností, prítomnosť škály), takže sa
 * musí prepísať celá otázka naraz.
 */
const QuestionEditor: React.FC<Props> = ({
  name,
  index,
  total,
  value,
  onUpdate,
  onDuplicate,
  onRemove,
  disabled = false,
}) => {
  const { questionType, hasFreeQuestion } = value;
  const isScale = questionType === 'scale';
  const isChoice =
    questionType === 'single_choice' || questionType === 'multi_choice';

  const minRemovable = isScale || hasFreeQuestion ? 1 : 2;

  return (
    <Stack gap={3}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'grey.600' }}>
          {`${__('Otázka')} ${index + 1} ${__('z')} ${total}`}
        </Typography>
        {!disabled && (
          <Stack direction='row' gap={0.5}>
            <Button
              variant='outline'
              size='small'
              startIcon={<ContentCopyIcon fontSize='small' />}
              onClick={onDuplicate}
            >
              {__('Duplikovať')}
            </Button>
            <Button
              variant='outline'
              size='small'
              startIcon={<DeleteOutlineIcon fontSize='small' />}
              onClick={onRemove}
            >
              {__('Zmazať')}
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack gap={1}>
        <SectionLabel>{__('Typ otázky')}</SectionLabel>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1,
          }}
        >
          {QUESTION_TYPES.map((type) => {
            const active = type === questionType;
            return (
              <Box
                key={type}
                component='button'
                type='button'
                disabled={disabled}
                onClick={() => {
                  if (active) return;
                  onUpdate(retypeQuestion(value, type));
                }}
                sx={({ palette, shape }) => ({
                  minHeight: { xs: 64, md: 76 },
                  display: 'flex',
                  flexDirection: { xs: 'row', md: 'column' },
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', md: 'center' },
                  gap: { xs: 1.25, md: 0.875 },
                  px: { xs: 1.5, md: 1 },
                  textAlign: 'left',
                  cursor: disabled ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  fontSize: { xs: 13, md: 12 },
                  fontWeight: active ? 700 : 500,
                  color: active ? palette.text.primary : palette.grey[600],
                  borderRadius: `${shape.borderRadius}px`,
                  border: `1px solid ${active ? palette.grey[900] : palette.divider}`,
                  backgroundColor: active
                    ? palette.grey[50]
                    : palette.background.paper,
                })}
              >
                <QuestionTypeIcon
                  type={type}
                  size={20}
                  color={active ? '#F73533' : undefined}
                />
                {questionTypeLabel(type)}
              </Box>
            );
          })}
        </Box>
      </Stack>

      <Stack gap={0.75}>
        <FormTextField
          sx={{ mb: 0 }}
          name={`${name}.title`}
          label={__('Text otázky')}
          required
          fullWidth
          maxLength={255}
          maxLengthIndicator={false}
          validate={composeValidators(required, (v) =>
            v && v.length > 255 ? __('Maximálne 255 znakov') : undefined,
          )}
          disabled={disabled}
        />
        <FormTextField
          sx={{ mb: 0 }}
          name={`${name}.description`}
          placeholder={__('Doplňujúci text k otázke (nepovinné)')}
          fullWidth
          maxLength={500}
          maxLengthIndicator={false}
          disabled={disabled}
        />
      </Stack>

      {questionType === 'multi_choice' && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={2}
          sx={({ palette, shape }) => ({
            border: `1px solid ${palette.divider}`,
            borderRadius: `${shape.borderRadius}px`,
            backgroundColor: palette.grey[50],
            p: 1.75,
          })}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 'none' }}>
            {__('Občan vyberie')}
          </Typography>
          <Stack direction='row' alignItems='center' gap={1}>
            <Box sx={{ width: 72 }}>
              <FormTextField
                sx={{ mb: 0 }}
                name={`${name}.minOptions`}
                type='number'
                min={1}
                fullWidth
                disabled={disabled}
                parse={(v) => (v === '' ? undefined : parseInt(v, 10))}
                validate={(v, values: { questions?: QuestionValues[] }) => {
                  const max = values.questions?.[index]?.maxOptions;
                  if (!v || v < 1) return __('Najmenej 1');
                  if (max && v > max)
                    return __('Minimum musí byť menšie ako maximum');
                  return undefined;
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {__('až')}
            </Typography>
            <Box sx={{ width: 72 }}>
              <FormTextField
                sx={{ mb: 0 }}
                name={`${name}.maxOptions`}
                type='number'
                min={1}
                fullWidth
                disabled={disabled}
                parse={(v) => (v === '' ? undefined : parseInt(v, 10))}
                validate={(v, values: { questions?: QuestionValues[] }) => {
                  const question = values.questions?.[index];
                  const available =
                    (question?.options.length ?? 0) +
                    (question?.hasFreeQuestion ? 1 : 0);
                  if (!v || v < 1) return __('Najmenej 1');
                  if (question?.minOptions && v < question.minOptions)
                    return __('Maximum musí byť väčšie ako minimum');
                  if (available && v > available)
                    return __('Viac, než koľko je možností');
                  return undefined;
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {`${__('z')} ${value.options.length + (hasFreeQuestion ? 1 : 0)} ${__('možností')}`}
            </Typography>
          </Stack>

          {/* Presne tá istá veta, akú uvidí občan nad možnosťami - inak sa
              limit nastavuje naslepo. */}
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              ml: { md: 'auto' },
              textAlign: { md: 'right' },
              maxWidth: 220,
            }}
          >
            {`${__('Občan uvidí:')} „${answerRuleHint(questionShape(value))}"`}
          </Typography>
        </Stack>
      )}

      {isScale && (
        <Stack
          gap={1.5}
          sx={({ palette, shape }) => ({
            border: `1px solid ${palette.divider}`,
            borderRadius: `${shape.borderRadius}px`,
            backgroundColor: palette.grey[50],
            p: 1.75,
          })}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5}>
            <Box sx={{ width: { md: 150 } }}>
              {/* Najnižšia hodnota je 0 alebo 1, nie ľubovoľné číslo: škála je
                  hodnotiaca stupnica, nie interval. Nula znamená plnohodnotné
                  „nepodporujem", nie chýbajúce hodnotenie - preto sa dá zvoliť
                  a preto to nie je to isté, čo nepovinné tvrdenie. */}
              <FormTextField
                sx={{ mb: 0 }}
                name={`${name}.scaleMin`}
                label={__('Najnižšia hodnota')}
                type='number'
                min={MIN_SCALE_MIN}
                max={MAX_SCALE_MIN}
                fullWidth
                disabled={disabled}
                parse={(v) => (v === '' ? undefined : parseInt(v, 10))}
                validate={(v) => {
                  if (v === undefined || v === '')
                    return __('Zadajte najnižšiu hodnotu škály');
                  if (v !== MIN_SCALE_MIN && v !== MAX_SCALE_MIN)
                    return `${__('Najnižšia hodnota môže byť')} ${MIN_SCALE_MIN} ${__('alebo')} ${MAX_SCALE_MIN}`;
                  return undefined;
                }}
              />
            </Box>
            <Box sx={{ width: { md: 150 } }}>
              <FormTextField
                sx={{ mb: 0 }}
                name={`${name}.scaleMax`}
                label={__('Najvyššia hodnota')}
                type='number'
                min={MIN_SCALE_MAX}
                max={MAX_SCALE_MAX}
                fullWidth
                disabled={disabled}
                parse={(v) => (v === '' ? undefined : parseInt(v, 10))}
                validate={(v) => {
                  if (v === undefined || v === '')
                    return __('Zadajte najvyššiu hodnotu škály');
                  if (v < MIN_SCALE_MAX || v > MAX_SCALE_MAX)
                    return `${__('Najvyššia hodnota musí byť od')} ${MIN_SCALE_MAX} ${__('do')} ${MAX_SCALE_MAX}`;
                  return undefined;
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormTextField
                sx={{ mb: 0 }}
                name={`${name}.scaleMinLabel`}
                label={__('Popis dolného konca (nepovinné)')}
                placeholder={__('napr. nesúhlasím')}
                fullWidth
                disabled={disabled}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormTextField
                sx={{ mb: 0 }}
                name={`${name}.scaleMaxLabel`}
                label={__('Popis horného konca (nepovinné)')}
                placeholder={__('napr. súhlasím')}
                fullWidth
                disabled={disabled}
              />
            </Box>
          </Stack>
        </Stack>
      )}

      {questionType !== 'free_text' && (
        <Stack gap={1}>
          <SectionLabel>
            {isScale ? __('Tvrdenia') : __('Odpovede')}
          </SectionLabel>

          <FieldArray name={`${name}.options`}>
            {({ fields }) => (
              <Stack gap={1.25}>
                {fields.map((optionName, optionIndex) => {
                  const option = fields.value?.[optionIndex] ?? { title: '' };

                  return (
                    <Stack key={optionName} gap={1}>
                      <Stack direction='row' alignItems='center' gap={1.25}>
                        {!isScale && (
                          <Box
                            aria-hidden
                            sx={({ palette, shape }) => ({
                              flex: 'none',
                              width: 20,
                              height: 20,
                              borderRadius:
                                questionType === 'single_choice'
                                  ? `${shape.borderRadiusFull}px`
                                  : `${shape.borderRadiusSm}px`,
                              border: `2px solid ${palette.grey[400]}`,
                            })}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <FormTextField
                            sx={{ mb: 0 }}
                            name={`${optionName}.title`}
                            placeholder={
                              isScale
                                ? `${optionIndex + 1}. ${__('tvrdenie')}`
                                : `${optionIndex + 1}. ${__('možnosť')}`
                            }
                            fullWidth
                            required
                            maxLength={255}
                            maxLengthIndicator={false}
                            validate={composeValidators(required, (v) =>
                              v && v.length > 255
                                ? __('Maximálne 255 znakov')
                                : undefined,
                            )}
                            disabled={disabled}
                          />
                        </Box>

                        {!disabled && (
                          <Stack
                            direction='row'
                            gap={0.5}
                            sx={{ flex: 'none' }}
                          >
                            {/* Tvrdenie škály odkaz nemá - hodnotí sa, neklikáva sa naň. */}
                            {!isScale && (
                              <IconButton
                                aria-label={
                                  option.hasUrl
                                    ? __('Odobrať odkaz')
                                    : __('Pridať odkaz')
                                }
                                onClick={() =>
                                  fields.update(optionIndex, {
                                    ...option,
                                    hasUrl: !option.hasUrl,
                                    url: option.hasUrl ? undefined : '',
                                  })
                                }
                              >
                                {option.hasUrl ? (
                                  <LinkOffIcon fontSize='small' />
                                ) : (
                                  <AddLinkIcon fontSize='small' />
                                )}
                              </IconButton>
                            )}
                            <IconButton
                              aria-label={__('Zmazať')}
                              disabled={fields.length <= minRemovable}
                              onClick={() => fields.remove(optionIndex)}
                            >
                              <DeleteOutlineIcon fontSize='small' />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>

                      {/* Popis a povinnosť má len tvrdenie škály. Pri možnosti
                          výberu popis nikto nevypĺňa a `required` by server
                          odmietol ako `VOTING_REQUIRED_NOT_ALLOWED`. */}
                      {isScale && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ sm: 'flex-start' }}
                          gap={1}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FormTextField
                              sx={{ mb: 0 }}
                              name={`${optionName}.description`}
                              placeholder={__('Popis tvrdenia (nepovinné)')}
                              fullWidth
                              multiline
                              maxLength={2048}
                              maxLengthIndicator={false}
                              validate={(v) =>
                                v && v.length > 2048
                                  ? __('Maximálne 2048 znakov')
                                  : undefined
                              }
                              disabled={disabled}
                            />
                          </Box>
                          <Box sx={{ flex: 'none' }}>
                            <FormCheckboxField
                              name={`${optionName}.optional`}
                              label={__('Nepovinné')}
                            />
                          </Box>
                        </Stack>
                      )}

                      {option.hasUrl && (
                        <Box sx={{ pl: isScale ? 0 : 4 }}>
                          <FormTextField
                            sx={{ mb: 0 }}
                            name={`${optionName}.url`}
                            placeholder={__('Odkaz pri možnosti')}
                            fullWidth
                            required
                            validate={composeValidators(required, isLink)}
                            disabled={disabled}
                          />
                        </Box>
                      )}
                    </Stack>
                  );
                })}

                {/* Možnosť „Iné" nie je typ otázky - je to príznak možnosti
                    a smie stáť pri oboch výberoch. */}
                {hasFreeQuestion && (
                  <Stack gap={1}>
                    <Stack direction='row' alignItems='center' gap={1.25}>
                      <Box
                        aria-hidden
                        sx={({ palette, shape }) => ({
                          flex: 'none',
                          width: 20,
                          height: 20,
                          borderRadius:
                            questionType === 'single_choice'
                              ? `${shape.borderRadiusFull}px`
                              : `${shape.borderRadiusSm}px`,
                          border: `2px dashed ${palette.grey[400]}`,
                        })}
                      />
                      <Box
                        sx={({ palette, shape, controlHeight }) => ({
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          height: controlHeight.large,
                          px: 1.5,
                          borderRadius: `${shape.borderRadius}px`,
                          border: `1px solid ${palette.divider}`,
                          backgroundColor: palette.grey[50],
                          fontSize: 15,
                          color: palette.grey[600],
                        })}
                      >
                        {__('Iné - občan dopíše vlastnú odpoveď')}
                      </Box>
                      {!disabled && (
                        <Stack direction='row' gap={0.5} sx={{ flex: 'none' }}>
                          <IconButton
                            aria-label={
                              value.freeQuestionHasUrl
                                ? __('Odobrať odkaz')
                                : __('Pridať odkaz')
                            }
                            onClick={() =>
                              onUpdate({
                                ...value,
                                freeQuestionHasUrl: !value.freeQuestionHasUrl,
                                freeQuestionUrl: value.freeQuestionHasUrl
                                  ? undefined
                                  : '',
                              })
                            }
                          >
                            {value.freeQuestionHasUrl ? (
                              <LinkOffIcon fontSize='small' />
                            ) : (
                              <AddLinkIcon fontSize='small' />
                            )}
                          </IconButton>
                          <IconButton
                            aria-label={__('Zmazať')}
                            disabled={fields.length < 2}
                            onClick={() =>
                              onUpdate({
                                ...value,
                                hasFreeQuestion: false,
                                freeQuestionHasUrl: false,
                                freeQuestionUrl: undefined,
                              })
                            }
                          >
                            <DeleteOutlineIcon fontSize='small' />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>

                    {value.freeQuestionHasUrl && (
                      <Box sx={{ pl: 4 }}>
                        <FormTextField
                          sx={{ mb: 0 }}
                          name={`${name}.freeQuestionUrl`}
                          placeholder={__('Odkaz pri otvorenej odpovedi')}
                          fullWidth
                          required
                          validate={composeValidators(required, isLink)}
                          disabled={disabled}
                        />
                      </Box>
                    )}
                  </Stack>
                )}

                {!disabled && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    gap={1}
                    alignItems={{ sm: 'center' }}
                    sx={{ mt: 0.5 }}
                  >
                    <Button
                      variant='outline'
                      size='small'
                      startIcon={<AddIcon fontSize='small' />}
                      onClick={() =>
                        fields.push(
                          isScale
                            ? { title: '' }
                            : { title: '', hasUrl: false },
                        )
                      }
                    >
                      {isScale ? __('Pridať tvrdenie') : __('Pridať možnosť')}
                    </Button>

                    {isChoice && !hasFreeQuestion && (
                      <Button
                        variant='text'
                        size='small'
                        startIcon={<AddIcon fontSize='small' />}
                        onClick={() =>
                          onUpdate({ ...value, hasFreeQuestion: true })
                        }
                      >
                        {__('Pridať otvorenú odpoveď')}
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
          </FieldArray>
        </Stack>
      )}

      {questionType === 'free_text' && (
        <Stack gap={1}>
          <SectionLabel>{__('Odpoveď')}</SectionLabel>
          <Typography variant='body2' color='text.secondary'>
            {__(
              'Občan napíše odpoveď vlastnými slovami. Otázka je nepovinná - dá sa preskočiť.',
            )}
          </Typography>
          {!disabled && (
            <Stack direction='row' gap={1} alignItems='center'>
              <Button
                variant='outline'
                size='small'
                startIcon={
                  value.freeQuestionHasUrl ? (
                    <LinkOffIcon fontSize='small' />
                  ) : (
                    <AddLinkIcon fontSize='small' />
                  )
                }
                onClick={() =>
                  onUpdate({
                    ...value,
                    freeQuestionHasUrl: !value.freeQuestionHasUrl,
                    freeQuestionUrl: value.freeQuestionHasUrl ? undefined : '',
                  })
                }
              >
                {value.freeQuestionHasUrl
                  ? __('Odobrať odkaz')
                  : __('Pridať odkaz')}
              </Button>
            </Stack>
          )}
          {value.freeQuestionHasUrl && (
            <FormTextField
              sx={{ mb: 0 }}
              name={`${name}.freeQuestionUrl`}
              placeholder={__('Odkaz k otázke')}
              fullWidth
              required
              validate={composeValidators(required, isLink)}
              disabled={disabled}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default QuestionEditor;
